import type { ConsoleEvalRun } from '@plato/shared';
import { useCallback, useEffect } from 'react';
import { DataTable } from '../../components/data-table.tsx';
import { formatDateTime } from '../../components/format.ts';
import { Loaded } from '../../components/page.tsx';
import { useReloadableTenantQuery } from '../../data/console-context.tsx';
import type { ConsoleData, TenantSummary } from '../../data/console-data.ts';
import { isFinished, OutcomeChip, outcomeOf } from './outcome.tsx';

export const POLL_MS = 3000;

const loadRuns = (data: ConsoleData, tenant: TenantSummary) => data.listEvalRuns(tenant.id);

export function RunsPanel(props: {
  openRun: string | null;
  onOpenRun: (id: string | null) => void;
}) {
  const { query, reload } = useReloadableTenantQuery(loadRuns);
  const going = query.status === 'ready' && query.value.some((run) => !isFinished(run.status));
  usePolling(going, reload);
  return (
    <section>
      {props.openRun !== null && (
        <RunDetail runId={props.openRun} onClose={() => props.onOpenRun(null)} />
      )}
      <Loaded query={query}>
        {(runs) => (
          <>
            <DataTable
              caption="Runs, newest first"
              columns={[
                { header: 'Started', cell: (row) => formatDateTime(row.created_at) },
                { header: 'Status', cell: (row) => <OutcomeChip status={row.status} /> },
                { header: 'Cases', cell: caseCounts },
                { header: 'Model', cell: (row) => row.model_id ?? '—' },
                {
                  header: 'Details',
                  cell: (row) => (
                    <button type="button" className="quiet" onClick={() => props.onOpenRun(row.id)}>
                      Open
                    </button>
                  ),
                },
              ]}
              rows={runs}
            />
            {runs.length === 0 && <p className="muted">No runs yet. Start one from Cases.</p>}
          </>
        )}
      </Loaded>
    </section>
  );
}

function RunDetail(props: { runId: string; onClose: () => void }) {
  const load = useCallback(
    (data: ConsoleData, tenant: TenantSummary) => data.getEvalRun(tenant.id, props.runId),
    [props.runId],
  );
  const { query, reload } = useReloadableTenantQuery(load);
  usePolling(query.status === 'ready' && !isFinished(query.value.run.status), reload);
  return (
    <section aria-label="Run detail">
      <div className="row">
        <h2>Run</h2>
        <button type="button" className="quiet" onClick={props.onClose}>
          Close
        </button>
      </div>
      <Loaded query={query}>
        {({ run, cases }) => (
          <>
            <p className="row">
              <OutcomeChip status={run.status} />
              <span className="muted">
                {caseCounts(run)} · {run.trials_per_case} trials per case
              </span>
            </p>
            {run.error !== null && <p className="notice notice-danger">{run.error}</p>}
            {cases.length === 0 && !isFinished(run.status) && (
              <p role="status">The first answers are on their way…</p>
            )}
            {cases.map((outcome) => (
              <article key={outcome.eval_case_id}>
                <h3 className="row">
                  {outcome.name} <OutcomeChip status={outcomeOf(outcome)} />
                </h3>
                <p className="muted">{outcome.question}</p>
                <DataTable
                  caption={`Trials of ${outcome.name}`}
                  columns={[
                    { header: 'Trial', cell: (row) => String(row.trial) },
                    { header: 'Outcome', cell: (row) => <OutcomeChip status={outcomeOf(row)} /> },
                    { header: "Judge's reasoning", cell: (row) => row.reasoning },
                    {
                      header: 'Trace',
                      cell: (row) =>
                        row.conversation_id !== null && row.user_id !== null ? (
                          <a href={`#/conversations/${row.conversation_id}?user=${row.user_id}`}>
                            Open trace
                          </a>
                        ) : (
                          '—'
                        ),
                    },
                  ]}
                  rows={outcome.trials.map((trial) => ({ ...trial, id: String(trial.trial) }))}
                />
                {outcome.trials.some((trial) => !trial.vendor_tools) && (
                  <p className="hint">
                    Answered without your product's tools: the case has no test account.
                  </p>
                )}
              </article>
            ))}
          </>
        )}
      </Loaded>
    </section>
  );
}

function caseCounts(run: ConsoleEvalRun): string {
  const parts = [`${run.passed_cases} of ${run.case_count} passed`];
  if (run.failed_cases > 0) parts.push(`${run.failed_cases} failed`);
  if (run.errored_cases > 0) parts.push(`${run.errored_cases} errored`);
  return parts.join(', ');
}

function usePolling(active: boolean, reload: () => void): void {
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(reload, POLL_MS);
    return () => clearInterval(timer);
  }, [active, reload]);
}
